import { createClient } from '@supabase/supabase-js';

// Constants for environment names
export const ENVIRONMENT = {
  DEV: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production'
};

// Project URLs and keys from environment variables
const SUPABASE_CONFIG = {
  [ENVIRONMENT.DEV]: {
    URL: process.env.REACT_APP_SUPABASE_URL_DEV,
    ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY_DEV,
    PROJECT_NAME: 'BagpipeBlasphemy-Dev'
  },
  [ENVIRONMENT.STAGING]: {
    URL: process.env.REACT_APP_SUPABASE_URL_STAGING,
    ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY_STAGING,
    PROJECT_NAME: 'BagpipeBlasphemy-Staging'
  },
  [ENVIRONMENT.PRODUCTION]: {
    URL: process.env.REACT_APP_SUPABASE_URL,
    ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY,
    PROJECT_NAME: 'BagpipeBlasphemy'
  }
};

// Determine the current environment
export const getCurrentEnvironment = () => {
  // Prefer explicit APP_ENV if set
  if (process.env.APP_ENV) {
    if (process.env.APP_ENV === 'development') return ENVIRONMENT.DEV;
    if (process.env.APP_ENV === 'staging') return ENVIRONMENT.STAGING;
    if (process.env.APP_ENV === 'production') return ENVIRONMENT.PRODUCTION;
  }
  // Check if running in a browser
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    if (process.env.NODE_ENV === 'development') {
      return ENVIRONMENT.DEV;
    } else if (hostname.includes('staging') || hostname.includes('test')) {
      return ENVIRONMENT.STAGING;
    } else {
      return ENVIRONMENT.PRODUCTION;
    }
  } else {
    if (process.env.NODE_ENV === 'development') {
      return ENVIRONMENT.DEV;
    } else if (process.env.NODE_ENV === 'staging' || process.env.NODE_ENV === 'test') {
      return ENVIRONMENT.STAGING;
    } else {
      return ENVIRONMENT.PRODUCTION;
    }
  }
};

// Get Supabase configuration for the current environment
export const getSupabaseConfig = (forceEnv = null) => {
  const env = forceEnv || getCurrentEnvironment();
  const config = SUPABASE_CONFIG[env];
  // Runtime checks for missing env vars
  if (env === ENVIRONMENT.STAGING) {
    if (!process.env.REACT_APP_SUPABASE_URL_STAGING || !process.env.REACT_APP_SUPABASE_ANON_KEY_STAGING) {
      throw new Error("Staging environment variables are missing! Refusing to fallback to production. Please set REACT_APP_SUPABASE_URL_STAGING and REACT_APP_SUPABASE_ANON_KEY_STAGING.");
    }
  }
  if (env === ENVIRONMENT.PRODUCTION) {
    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
      throw new Error("Production environment variables are missing! Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.");
    }
  }
  return config;
};

// Create a Supabase client for the current environment
export const createSupabaseClient = (forceEnv = null) => {
  const config = getSupabaseConfig(forceEnv);
  
  // Check if required environment variables are available
  if (!config.URL || !config.ANON_KEY) {
    console.error(`Supabase configuration missing for environment: ${forceEnv || getCurrentEnvironment()}`);
    console.error('Make sure to set the required environment variables in .env.local');
    
    // Return a dummy client that will log errors instead of crashing
    return {
      from: () => ({
        select: () => Promise.resolve({ data: null, error: 'Supabase configuration missing' }),
        insert: () => Promise.resolve({ data: null, error: 'Supabase configuration missing' }),
        update: () => Promise.resolve({ data: null, error: 'Supabase configuration missing' }),
        delete: () => Promise.resolve({ data: null, error: 'Supabase configuration missing' }),
      }),
    };
  }
  
  return createClient(config.URL, config.ANON_KEY);
};

// Default client instance for the current environment
const supabase = createSupabaseClient();

export default supabase; 